import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, ExternalLink, ImagePlus, Loader2, Palette, Radar, Save, Share2, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { Rules } from "@/components/dashboard/Rules";
import { CS } from "@/constants/testIds/clickshield";

const STEPS = [
  { n: "01", t: "Copy your snippet", d: "One line of JavaScript, unique to your account." },
  { n: "02", t: "Paste before </head>", d: "On every landing page your ads point to." },
  { n: "03", t: "Watch threats appear", d: "Every visit is scored in real time — bots get redirected away." },
];

export const Tracking = () => {
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [redirectEnabled, setRedirectEnabled] = useState(true);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [shareUrl, setShareUrl] = useState(null);
  const [brand, setBrand] = useState({ brand_name: "", logo: "" });
  const [brandSaving, setBrandSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/tracking/settings").then((r) => {
      setSettings(r.data);
      setRedirectEnabled(r.data.redirect_enabled);
      setRedirectUrl(r.data.redirect_url);
    }).catch(() => {});
    api.get("/status/share-link").then((r) => setShareUrl(r.data)).catch(() => {});
    api.get("/branding").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const onLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo must be under 1MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBrand((b) => ({ ...b, logo: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveBranding = async () => {
    setBrandSaving(true);
    try {
      const { data } = await api.patch("/branding", brand);
      setBrand(data);
      toast.success("Branding saved — your status page is updated");
    } catch (err) {
      toast.error("Could not save branding");
    } finally {
      setBrandSaving(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl.share_url);
    toast.success("LinkedIn-ready link copied — it unfurls with your branded shield card");
  };

  const copy = async () => {
    await navigator.clipboard.writeText(settings.snippet);
    setCopied(true);
    toast.success("Snippet copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/tracking/settings", { redirect_enabled: redirectEnabled, redirect_url: redirectUrl });
      toast.success("Protection settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
      </div>
    );

  const live = settings.total_events > 0;

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 rounded-xl bg-[#0B101A] border border-white/8 p-6">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-cyan-neon" />
            <h3 className="font-display text-lg text-white">Install your tracking snippet</h3>
          </div>
          <p className="text-sm text-slate-400 mb-5">
            Add this to every page your ads land on. It fingerprints each visit, scores it for fraud, and redirects confirmed bots away.
          </p>
          <div className="relative rounded-lg bg-[#040914] border border-white/10 p-4">
            <code data-testid={CS.trackingSnippet} className="font-mono text-[12px] text-cyan-neon break-all leading-relaxed block pr-10">
              {settings.snippet}
            </code>
            <button
              data-testid={CS.trackingCopyBtn}
              onClick={copy}
              className="absolute top-3 right-3 p-2 rounded-md border border-white/10 text-slate-400 hover:text-cyan-neon hover:border-cyan-neon/40 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-[#39FF14]" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="font-mono text-[11px] text-slate-500">
              Site key: <span data-testid={CS.trackingSiteKey} className="text-white">{settings.site_key}</span>
            </span>
            <span className="font-mono text-[11px] text-slate-500">
              Events received: <span data-testid={CS.trackingEventsCount} className="text-white">{settings.total_events.toLocaleString()}</span>
            </span>
            <span
              data-testid={CS.trackingDataSource}
              className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-md border ${
                live ? "text-[#39FF14] bg-[#39FF14]/10 border-[#39FF14]/25" : "text-amber-400 bg-amber-500/10 border-amber-500/25"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-[#39FF14] animate-pulse-glow" : "bg-amber-400"}`} />
              {live ? "Live tracking active" : "Awaiting first event · demo data shown"}
            </span>
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-lg bg-[#040914] border border-white/8 p-4">
                <div className="font-mono text-[10px] text-cyan-neon">{s.n}</div>
                <div className="text-sm text-white mt-1">{s.t}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-lg bg-[#040914] border border-[#39FF14]/15 p-4">
            <Share2 className="w-4 h-4 text-[#39FF14]" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white">Public shield status page</div>
              <div className="text-xs text-slate-500 mt-0.5">A shareable, no-login page proving your ads are protected — links unfurl with a branded preview card on LinkedIn.</div>
            </div>
            <button
              data-testid="shield-copy-link-btn"
              onClick={copyShareLink}
              disabled={!shareUrl}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#39FF14]/30 text-[#39FF14] text-sm font-semibold hover:bg-[#39FF14]/10 transition-colors disabled:opacity-50"
            >
              <Copy className="w-3.5 h-3.5" /> Copy link
            </button>
            {shareUrl && (
              <a
                data-testid="shield-open-link"
                href={shareUrl.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-slate-300 text-sm hover:border-cyan-neon/50 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </a>
            )}
            {shareUrl && (
              <a
                data-testid="shield-preview-card-link"
                href={`${shareUrl.share_url.replace("/share/", "/card/")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-slate-300 text-sm hover:border-cyan-neon/50 transition-colors"
              >
                <ImagePlus className="w-3.5 h-3.5" /> Preview card
              </a>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
          className="rounded-xl bg-[#0B101A] border border-white/8 p-6 h-fit">
          <div className="flex items-center gap-2 mb-5">
            <Radar className="w-4 h-4 text-[#39FF14]" />
            <h3 className="font-display text-lg text-white">Bot handling</h3>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-[#040914] border border-white/8 p-4 mb-4">
            <div>
              <div className="text-sm text-white">Redirect blocked visitors</div>
              <div className="text-xs text-slate-500 mt-0.5">Send confirmed bots away from your page</div>
            </div>
            <Switch data-testid={CS.trackingRedirectToggle} checked={redirectEnabled} onCheckedChange={setRedirectEnabled} />
          </div>
          <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Redirect destination</label>
          <input
            data-testid={CS.trackingRedirectUrl}
            value={redirectUrl}
            onChange={(e) => setRedirectUrl(e.target.value)}
            disabled={!redirectEnabled}
            placeholder="https://www.google.com"
            className="mt-2 w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white text-sm placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors disabled:opacity-50"
          />
          <button
            data-testid={CS.trackingSaveBtn}
            onClick={save}
            disabled={saving}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-cyan-neon text-[#040914] font-semibold text-sm py-3 hover:brightness-110 transition-[filter] disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save settings
          </button>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            Scoring engine checks headless browsers, datacenter &amp; proxy IPs, click velocity, missing ad-click IDs and more. Score ≥ 70 = Blocked, ≥ 45 = Flagged.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="lg:col-start-3 rounded-xl bg-[#0B101A] border border-white/8 p-6 h-fit">
          <div className="flex items-center gap-2 mb-1">
            <Palette className="w-4 h-4 text-cyan-neon" />
            <h3 className="font-display text-lg text-white">Status page branding</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Put your agency&apos;s name and logo on the public shield status page you share with clients.
          </p>
          <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Brand name</label>
          <input
            data-testid="branding-name-input"
            value={brand.brand_name}
            onChange={(e) => setBrand((b) => ({ ...b, brand_name: e.target.value }))}
            maxLength={60}
            placeholder="Acme Media Agency"
            className="mt-2 w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white text-sm placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors"
          />
          <div className="mt-4 flex items-center gap-3">
            {brand.logo ? (
              <img data-testid="branding-logo-preview" src={brand.logo} alt="Brand logo"
                className="w-12 h-12 rounded-lg object-contain bg-white/5 border border-white/10 p-1" />
            ) : (
              <div className="w-12 h-12 rounded-lg border border-dashed border-white/15 flex items-center justify-center">
                <ImagePlus className="w-5 h-5 text-slate-600" />
              </div>
            )}
            <input ref={fileRef} data-testid="branding-logo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoPick} />
            <button
              data-testid="branding-upload-btn"
              onClick={() => fileRef.current?.click()}
              className="px-4 py-2 rounded-lg border border-white/15 text-slate-300 text-sm hover:border-cyan-neon/50 transition-colors"
            >
              Upload logo
            </button>
            {brand.logo && (
              <button
                data-testid="branding-remove-logo"
                onClick={() => setBrand((b) => ({ ...b, logo: "" }))}
                className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="font-mono text-[10px] text-slate-600 mt-2">PNG, JPG or WEBP · max 1MB</p>
          <button
            data-testid="branding-save-btn"
            onClick={saveBranding}
            disabled={brandSaving}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg border border-cyan-neon/30 text-cyan-neon font-semibold text-sm py-3 hover:bg-cyan-neon/10 transition-colors disabled:opacity-60"
          >
            {brandSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save branding
          </button>
        </motion.div>
      </div>
      <Rules />
    </div>
  );
};
